import {
  Box,
  Button,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField
} from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons';
import { Card, CardTitle } from 'apps/wrapper/src/components/shared/card/card.style';
import { Dialog } from 'apps/wrapper/src/components/shared/dialog/dialog.styles';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { usePatientProfile } from '../../../../../../../hooks/use-patient-profile';
import { ContactApi } from '../../../../../../../services/api-client/contact-api';
import { SecureAttachment } from '../../../../../../../services/api-client/contact-api.types';
import { DirectCommunicationType } from '../../../../../../../services/practice-portal/direct-communication/types';
import { downloadFile } from '../../../../../../../utils/file/download-file';
import { OrangeGreenSwitch } from '../../../../../../shared/switch/switch.styles';
import { DirectMailTemplateEditorDialog } from '../../../../../../shared/template/template-editor/direct-mail-template-editor-dialog';
import {
  ErrorList,
  ErrorListItem,
  ErrorMessage,
  ExclamationIcon,
  ValidationSummary
} from '../../../../../../shared/validation/validation.styles';
import { CloseDialogButton, DialogContentWrapper, UploadButtonWrapper } from './add-file.styles';
import { initialFormState } from './intial-form-state';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes

export interface SecureAttachmentForm {
  file: any;
  fileName: string;
  displayName: string;
  description: string;
}

export interface AddFileProps {
  opened: boolean;
  attachment?: SecureAttachment;
  patientId: number;
  locationName: string | undefined;
  onClose: () => void;
  onChange: () => void;
}

export const AddFileDialog: FC<AddFileProps> = (props) => {
  const { opened, onClose, attachment, patientId, locationName, onChange } = props;
  const { showError } = useNotification();

  const {
    contactDetails,
    contactLocation,
    patientInfo,
    loggedInUserLocation,
    locationId,
    contactLocationInfo,
    networkId
  } = usePatientProfile();

  const { register, handleSubmit, formState, clearErrors, setError } = useForm({ mode: 'all' });
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState<SecureAttachmentForm>(initialFormState());
  const [notifyPatient, setNotifyPatient] = useState(false);
  const [notifyPatientEnabled, setNotifyPatientEnabled] = useState(false);
  const [templateEditorOpened, setTemplateEditorOpened] = useState(false);
  const contactApi = ContactApi.getInstance();

  useEffect(() => {
    setFormData(
      attachment
        ? {
            file: null,
            fileName: attachment.fileName,
            displayName: attachment.displayName,
            description: attachment.description
          }
        : initialFormState()
    );
  }, [opened, attachment]);

  useEffect(() => {
    if (!contactDetails || !contactLocation || !patientInfo || !loggedInUserLocation) {
      setNotifyPatientEnabled(false);
    } else {
      const samePatientInMultipleLocations =
        contactLocationInfo.length > 1 || !contactLocationInfo.filter((c) => c.networkId === networkId).length;

      const isContactLocationValid = contactLocation?.isCommunicationActive && contactLocation?.accountStatus === 0;
      const sameNetworkContact = contactDetails?.networkId === loggedInUserLocation?.idNetwork;

      setNotifyPatientEnabled(
        isContactLocationValid && (sameNetworkContact || samePatientInMultipleLocations) && contactDetails.isEmailValid
      );
    }
  }, [contactDetails, contactLocation, patientInfo, loggedInUserLocation]);

  const updateFormData = (state: Partial<SecureAttachmentForm>) => {
    setFormData({ ...formData, ...state });
  };

  const handleFileChange = (event: any) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('file', { type: 'custom', message: 'File size exceeds 100MB limit.' });
      updateFormData({ file: undefined, fileName: undefined });
    } else {
      clearErrors('file');
      updateFormData({ file: selectedFile, fileName: selectedFile.name });
    }
  };

  const handleSaveClick = async () => {
    if (Object.keys(formState.errors).length > 0) return;

    try {
      setIsSaving(true);

      const payload = new FormData();

      payload.append('file', formData.file);
      payload.append('displayName', formData.displayName);
      payload.append('description', formData.description);

      attachment
        ? await contactApi.updateSecureAttachment(contactDetails.id, locationId, attachment.fileName, payload)
        : await contactApi.createSecureAttachment(contactDetails.id, locationId, payload);

      if (notifyPatient) {
        setTemplateEditorOpened(true);
      } else {
        onChange();
      }
    } catch (e) {
      showError('Unable to save file.');
      handleClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadClick = async () => {
    if (!attachment) return;

    try {
      setIsDownloading(true);
      const file = await contactApi.getSecureAttachment(contactDetails.id, locationId, attachment.fileName);

      downloadFile(file.downloadUrl, attachment.fileName);
    } catch (e) {
      showError('Unable to get patient file.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!attachment) return;

    try {
      setIsDeleting(true);
      await contactApi.deleteSecureAttachment(contactDetails.id, locationId, attachment.fileName);
      onChange();
    } catch (e) {
      showError('Unable to delete patient file.');
    } finally {
      setIsDeleting(false);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData(initialFormState());
    clearErrors();
    onClose();
  };

  const handleTemplateEditorClose = () => {
    setTemplateEditorOpened(false);
    handleClose();
  };

  const handleTemplateEditorSend = () => {
    setTemplateEditorOpened(false);
    onChange();
  };

  return (
    <>
      <Dialog open={opened} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            {contactDetails.firstName} {contactDetails.lastName}
            <CloseDialogButton onClick={handleClose} />
          </DialogTitle>
          <DialogContent>
            <Card>
              <CardContent>
                <CardTitle sx={{ paddingLeft: '5px' }}>{`${attachment ? 'Edit' : 'Add'} File`}</CardTitle>
                The patient will be able to view this file under the "My Files" tab of the Patient Portal
                <form noValidate onSubmit={handleSubmit(handleSaveClick)}>
                  {Object.keys(formState.errors).length > 0 && (
                    <ValidationSummary>
                      <ExclamationIcon />
                      <ErrorMessage>Please correct the errors to continue:</ErrorMessage>
                      <ErrorList>
                        {Object.keys(formState.errors).map((key) => (
                          <ErrorListItem key={key}>{String(formState?.errors[key]?.message)}</ErrorListItem>
                        ))}
                      </ErrorList>
                    </ValidationSummary>
                  )}
                  <UploadButtonWrapper>
                    <input
                      id="file-upload"
                      accept="*"
                      style={{ display: 'none' }}
                      type="file"
                      {...register('file', {
                        required: attachment ? false : { value: true, message: 'File is required' }
                      })}
                      onChange={handleFileChange}
                    />
                    <label htmlFor="file-upload" style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        component="span"
                        color="primary"
                        size="medium"
                        sx={{ marginRight: '10px' }}
                        disableElevation
                      >
                        Upload File
                      </Button>
                      <Box sx={{ fontSize: '16px' }}>{formData.fileName ? formData.fileName : 'Choose a file'}</Box>
                    </label>
                  </UploadButtonWrapper>
                  <TextField
                    label="Display Name"
                    value={formData.displayName}
                    margin="normal"
                    variant="outlined"
                    size="small"
                    sx={{ width: '220px' }}
                    error={!!formState.errors.displayName}
                    {...register('displayName', {
                      required: { value: true, message: 'Display Name is required' }
                    })}
                    onChange={(e) => updateFormData({ displayName: e.target.value })}
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    margin="normal"
                    variant="outlined"
                    minRows={3}
                    multiline
                    fullWidth
                    error={!!formState.errors.description}
                    {...register('description', {
                      required: { value: true, message: 'Description is required' }
                    })}
                    onChange={(e) => updateFormData({ description: e.target.value })}
                  />
                  <FormControlLabel
                    label={<Box sx={{ fontSize: '13px', color: '#1C3D64' }}>Notify Patient</Box>}
                    control={
                      <OrangeGreenSwitch
                        color="default"
                        checked={notifyPatient}
                        disabled={!notifyPatientEnabled}
                        onChange={() => setNotifyPatient(!notifyPatient)}
                      />
                    }
                  />
                  <DialogActions sx={{ padding: 0, marginTop: '35px' }}>
                    <SecondaryButton
                      onClick={handleClose}
                      color="primary"
                      variant="outlined"
                      disableElevation
                      sx={{ marginRight: 'auto' }}
                    >
                      CANCEL
                    </SecondaryButton>
                    {attachment && (
                      <>
                        <SecondaryButton
                          loading={isDownloading}
                          onClick={handleDownloadClick}
                          disabled={isSaving || isDeleting}
                          color="primary"
                          variant="outlined"
                          disableElevation
                          sx={{ marginRight: '10px' }}
                        >
                          DOWNLOAD
                        </SecondaryButton>
                        <SecondaryButton
                          loading={isDeleting}
                          disabled={isSaving || isDownloading}
                          onClick={handleDeleteClick}
                          color="primary"
                          variant="outlined"
                          disableElevation
                          sx={{ marginRight: '10px' }}
                        >
                          DELETE
                        </SecondaryButton>
                      </>
                    )}
                    <PrimaryButton
                      type="submit"
                      loading={isSaving}
                      disabled={isDeleting || isDownloading || Object.keys(formState.errors).length > 0}
                      color="primary"
                      variant="contained"
                      disableElevation
                    >
                      SAVE
                    </PrimaryButton>
                  </DialogActions>
                </form>
              </CardContent>
            </Card>
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>

      <DirectMailTemplateEditorDialog
        open={templateEditorOpened}
        onClose={handleTemplateEditorClose}
        onSend={handleTemplateEditorSend}
        patientId={patientId}
        locationId={locationId}
        directCommunicationType={DirectCommunicationType.PatientNotification}
        title="Send Patient Notification"
        subject="New file added to your Patient Profile"
        fromName={locationName}
        attachmentFileName={formData.fileName}
        buttons={{ submit: { title: 'Send' } }}
      />
    </>
  );
};
